/**
 * author-sphere.ts — sfera 3D condivisa per i nodi-autore (Mappa dei temi e
 * Rete delle influenze).
 *
 * LOGICA COLORE = PERIODO letterario dell'autore: ogni epoca ha la sua tinta,
 * così le sfere non sono più tutte uguali e l'epoca si legge a colpo d'occhio
 * (e si accorda con le fasce dei secoli della timeline). Il bordeaux brand
 * resta riservato a selezione/evidenziazione (anelli), non al riempimento.
 *
 * PROFONDITÀ 3D (marcata): gradiente radiale con highlight caldo in alto-sx,
 * terminatore molto scuro in basso-dx, riflesso speculare a due livelli e una
 * "luce di rimbalzo" sul bordo basso (il dettaglio che dà la rotondità) + ombra
 * portata profonda. Helper esportati così entrambe le mappe rendono identico.
 */

/**
 * Tinta per periodo (mid-tone; il gradiente schiarisce/scurisce da qui).
 *
 * Assegnazione PER ANNO DI CORSO (decisione di Francesco, 26/08/2026): una
 * famiglia di tinta per ciascuno dei tre anni del triennio, e dentro la
 * famiglia il periodo varia di valore. In aula la sfera dice subito a che anno
 * appartiene l'autore, che è l'informazione che serve al docente.
 *
 * ⚠ Nessuna di queste sette tocca il blu ardesia del marchio (#2b4c6b) né una
 * delle cinque tinte di categoria (src/data/category-colors.ts): le sfere e i
 * nodi-categoria convivono nella mappa dei temi, e devono restare due assi
 * distinti. Le tinte-periodo stanno nelle bande di tinta che le categorie
 * lasciano libere — bronzo, vinaccia, verde salvia.
 */
const PERIOD_COLOR: Record<string, string> = {
  // Terzo anno · età arcaica e repubblicana — bronzo
  'eta-arcaica':         '#6b5641',
  'eta-repubblicana':    '#8a7154',
  // Quarto anno · età di Cesare e di Augusto — vinaccia
  'eta-augustea':        '#7a4a63',
  // Quinto anno · età imperiale e tardoantica — verde salvia
  'eta-giulio-claudia':  '#46705e',
  'eta-flavia-traianea': '#5a8271',
  'eta-antonina':        '#6f9485',
  'eta-tardoantica':     '#85a699',
};
/** Periodo assente o fuori vocabolario: grigio neutro, non una tinta di nessun anno. */
const FALLBACK = '#6a6a72';

/** Colore-sfera di un autore dal suo primo periodo (fallback neutro). */
export function authorSphereColor(periods: readonly string[] | undefined): string {
  const p = periods && periods[0];
  return (p && PERIOD_COLOR[p]) || FALLBACK;
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function mix(a: [number, number, number], b: [number, number, number], t: number): string {
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r},${g},${bl})`;
}
const lighten = (hex: string, t: number) => mix(hexToRgb(hex), [255, 255, 255], t);
const darken = (hex: string, t: number) => mix(hexToRgb(hex), [0, 0, 0], t);

/** id stabile di gradiente da un colore (per i <defs>). */
export function sphereGradId(hex: string): string { return 'asph-' + hex.replace('#', ''); }

/** Stop del gradiente sfera per una tinta (riuso DOM + stringa). */
export function sphereStops(hex: string): Array<{ offset: string; color: string }> {
  return [
    { offset: '0%', color: lighten(hex, 0.74) },
    { offset: '15%', color: lighten(hex, 0.36) },
    { offset: '54%', color: hex },
    { offset: '100%', color: darken(hex, 0.56) },
  ];
}
/** Colore della luce di rimbalzo sul bordo basso. */
export const sphereRim = (hex: string) => lighten(hex, 0.62);

/** <defs> con un gradiente per ogni tinta usata + l'ombra condivisa (id auth-sh). */
export function authorSphereDefs(colors: string[]): string {
  const uniq = [...new Set(colors)];
  const grads = uniq.map((c) => `<radialGradient id="${sphereGradId(c)}" cx="33%" cy="25%" r="84%">
      ${sphereStops(c).map((s) => `<stop offset="${s.offset}" stop-color="${s.color}"/>`).join('')}
    </radialGradient>`).join('');
  const filt = `<filter id="auth-sh" x="-70%" y="-70%" width="240%" height="240%">
      <feDropShadow dx="0" dy="3.5" stdDeviation="4" flood-color="#0b1016" flood-opacity="0.40"/>
    </filter>`;
  return grads + filt;
}

/** Sfera autore (stringa SVG) con la tinta `baseHex` (vedi authorSphereDefs). */
export function authorSphere(cx: number, cy: number, r: number, ini: string, fp: number, baseHex: string): string {
  const rim = sphereRim(baseHex);
  const safe = ini.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));
  return `<g>
    <circle cx="${cx}" cy="${cy}" r="${r}" fill="url(#${sphereGradId(baseHex)})" filter="url(#auth-sh)"/>
    <ellipse cx="${cx - r * 0.30}" cy="${cy - r * 0.36}" rx="${r * 0.48}" ry="${r * 0.32}" fill="rgba(255,255,255,0.50)" pointer-events="none"/>
    <ellipse cx="${cx - r * 0.34}" cy="${cy - r * 0.42}" rx="${r * 0.18}" ry="${r * 0.12}" fill="rgba(255,255,255,0.92)" pointer-events="none"/>
    <path d="M ${cx - r * 0.66} ${cy + r * 0.48} A ${r * 0.82} ${r * 0.82} 0 0 0 ${cx + r * 0.66} ${cy + r * 0.48}"
          fill="none" stroke="${rim}" stroke-opacity="0.66" stroke-width="${r * 0.13}" stroke-linecap="round" pointer-events="none"/>
    <text x="${cx}" y="${cy + fp * 0.34}" text-anchor="middle" font-family="Fraunces, serif" font-weight="700" font-size="${fp}" fill="#fff" pointer-events="none">${safe}</text>
  </g>`;
}
